import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { adminGet, adminDelete, adminUpload, reportError, AGENCY_ID } from './adminApi';

const EMPTY = { agency: AGENCY_ID, titre: '', contenu: '', lien: '', date: '' };

export function AdminNewsCard({ adminKey }) {
  const [news, setNews] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [image, setImage] = useState(null);
  const imageRef = useRef(null);

  const fetchNews = useCallback(async () => {
    try {
      const res = await adminGet('/api/admin/agency-news', adminKey);
      setNews(res.data.news || []);
    } catch (err) {
      reportError('actualités', err);
    }
  }, [adminKey]);

  useEffect(() => { fetchNews(); }, [fetchNews]);

  const add = async () => {
    if (!form.titre.trim() || !form.contenu.trim()) { toast.error('Titre et contenu obligatoires'); return; }
    try {
      const fd = new FormData();
      fd.append('agency', form.agency);
      fd.append('titre', form.titre);
      fd.append('contenu', form.contenu);
      fd.append('lien', form.lien);
      fd.append('date', form.date || new Date().toISOString().split('T')[0]);
      if (image) fd.append('image', image);
      await adminUpload('post', '/api/admin/agency-news', adminKey, fd);
      toast.success('Actualité ajoutée');
      setForm(EMPTY); setImage(null);
      fetchNews();
    } catch (err) {
      reportError('ajout actualité', err, (m) => toast.error(m || "Erreur lors de l'ajout"));
    }
  };

  const remove = async (id) => {
    try {
      await adminDelete(`/api/admin/agency-news/${id}`, adminKey);
      toast.success('Actualité supprimée');
      fetchNews();
    } catch (err) {
      reportError('suppression actualité', err, () => toast.error('Erreur lors de la suppression'));
    }
  };

  return (
    <>
      <Card className="p-5">
        <h3 className="font-['Space_Grotesk'] font-semibold mb-4">Ajouter une actualité</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-medium block mb-1">Agence</label>
            <Select value={form.agency} onValueChange={(v) => setForm((p) => ({ ...p, agency: v }))}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={AGENCY_ID}>Artois.Ptitservices</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Date</label>
            <Input type="date" className="h-9" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} />
          </div>
        </div>
        <div className="space-y-3 mb-3">
          <div>
            <label className="text-xs font-medium block mb-1">Titre *</label>
            <Input className="h-9" value={form.titre} onChange={(e) => setForm((p) => ({ ...p, titre: e.target.value }))} placeholder="Titre du post" />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Contenu *</label>
            <Input className="h-9" value={form.contenu} onChange={(e) => setForm((p) => ({ ...p, contenu: e.target.value }))} placeholder="Texte du post..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium block mb-1">Lien (optionnel)</label>
              <Input className="h-9" value={form.lien} onChange={(e) => setForm((p) => ({ ...p, lien: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Image</label>
              <input type="file" accept="image/*" ref={imageRef} className="hidden" onChange={(e) => setImage(e.target.files?.[0] || null)} />
              <Button variant="outline" size="sm" className="w-full h-9" onClick={() => imageRef.current?.click()}>
                <Image className="h-3.5 w-3.5 mr-1" /> {image ? 'Image OK' : 'Choisir'}
              </Button>
            </div>
          </div>
        </div>
        <Button size="sm" onClick={add}>Publier l'actualité</Button>
      </Card>

      {news.length === 0 ? (
        <Card className="p-8 text-center"><p className="text-muted-foreground">Aucune actualité.</p></Card>
      ) : (
        <div className="space-y-3">
          {news.map((n) => (
            <Card key={n._id} className="p-4 flex items-start gap-4">
              {n.image && (
                <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0"><img src={n.image} alt="" className="w-full h-full object-cover" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="secondary" className="text-[10px]">{n.agency}</Badge>
                  <span className="text-xs text-muted-foreground">{n.date}</span>
                </div>
                <p className="font-semibold text-sm">{n.titre}</p>
                <p className="text-xs text-muted-foreground line-clamp-1">{n.contenu}</p>
              </div>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 flex-shrink-0" onClick={() => remove(n._id)} aria-label="Supprimer"><XCircle className="h-4 w-4" /></Button>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
